/**
 * Controlled JSON textarea with inline validation. The parent owns the raw
 * string; `onValidChange` reports the parsed value (or null when empty/invalid).
 * Used for property_schema / properties editing (raw-JSON editor — schema-driven
 * dynamic forms deferred).
 */
import { useState, type ReactNode } from "react";

export interface JsonFieldProps {
  label: string;
  value: string;
  onChange: (raw: string) => void;
  /** Called with the parsed object, or null if empty/invalid. */
  onValidChange?: (parsed: Record<string, unknown> | null, valid: boolean) => void;
  rows?: number;
  placeholder?: string;
  allowEmpty?: boolean;
}

export function parseJsonObject(raw: string): { ok: boolean; value: Record<string, unknown> | null; error?: string } {
  const trimmed = raw.trim();
  if (trimmed === "") return { ok: true, value: null };
  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return { ok: false, value: null, error: "Must be a JSON object ({ … })." };
    }
    return { ok: true, value: parsed as Record<string, unknown> };
  } catch (e) {
    return { ok: false, value: null, error: e instanceof Error ? e.message : "Invalid JSON." };
  }
}

export function JsonField({
  label,
  value,
  onChange,
  onValidChange,
  rows = 6,
  placeholder = "{}",
}: JsonFieldProps): ReactNode {
  const [error, setError] = useState<string | null>(null);

  function handle(raw: string) {
    onChange(raw);
    const res = parseJsonObject(raw);
    setError(res.ok ? null : (res.error ?? "Invalid JSON."));
    onValidChange?.(res.value, res.ok);
  }

  return (
    <label className="block text-sm">
      <span className="mb-1 block text-slate-600">{label}</span>
      <textarea
        value={value}
        onChange={(e) => handle(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        spellCheck={false}
        className={`w-full rounded border px-2 py-1.5 font-mono text-xs ${
          error ? "border-red-400" : "border-slate-300"
        }`}
      />
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}
